import React, { useState } from 'react';
import UserService from './service';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone: string;
  phone_number: string;
  role_id: number;
  active: boolean;
  otpMethod: string;
  type_send_otp: string;
  is_submerchant: boolean;
  date_of_birth: string;
}

interface UserEditModalProps {
  user: User;
  onClose: () => void;
  onUserUpdated: () => void;
}

const UserEditModal: React.FC<UserEditModalProps> = ({ user, onClose, onUserUpdated }) => {
  const [updatedUser, setUpdatedUser] = useState({ ...user });
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    if (name === 'email') {
      setEmailError(!/^\S+@\S+\.\S+$/.test(value));
    }

    setUpdatedUser({ ...updatedUser, [name]: value });
  };

  const handleSubmit = async () => {
    if (emailError) return;

    setIsLoading(true);
    try {
      const payload = {
        id: updatedUser.id,
        email: updatedUser.email,
        role_id: Number(updatedUser.role_id),
        active: updatedUser.active,
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        otpMethod: updatedUser.otpMethod,
        type_send_otp: updatedUser.type_send_otp,
        is_submerchant: updatedUser.is_submerchant,
        date_of_birth: updatedUser.date_of_birth,
        phone_number: updatedUser.phone_number || '',
        mobile_phone: updatedUser.mobile_phone || '',
      };

      await UserService.updateUser(updatedUser.id, payload);
      await new Promise((resolve) => setTimeout(resolve, 500)); // Pequeña pausa para el loader
      onUserUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-80'>
      <div className='bg-gray-800 rounded-lg p-6 w-96 space-y-4 text-white'>
        <h2 className='text-lg font-semibold'>Edit User</h2>

        <div className='flex gap-2'>
          <input
            type='text'
            name='first_name'
            value={updatedUser.first_name}
            onChange={handleInputChange}
            placeholder='First Name'
            className='text-white w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring focus:ring-blue-500'
          />
          <input
            type='text'
            name='last_name'
            value={updatedUser.last_name}
            onChange={handleInputChange}
            placeholder='Last Name'
            className='text-white w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring focus:ring-blue-500'
          />
        </div>

        <input
          type='email'
          name='email'
          value={updatedUser.email}
          onChange={handleInputChange}
          placeholder='Email'
          className={`text-white w-full px-3 py-2 bg-gray-700 border ${
            emailError ? 'border-red-500' : 'border-gray-600'
          } rounded focus:outline-none focus:ring focus:ring-blue-500`}
        />
        {emailError && (
          <p className='text-red-500 text-sm mt-1'>Please enter a valid email address.</p>
        )}

        <div className='flex gap-2'>
          <input
            type='text'
            name='phone_number'
            value={updatedUser.phone_number}
            onChange={handleInputChange}
            placeholder='Phone Number'
            className='text-white w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring focus:ring-blue-500'
          />
          <input
            type='text'
            name='mobile_phone'
            value={updatedUser.mobile_phone}
            onChange={handleInputChange}
            placeholder='Mobile Phone'
            className='text-white w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring focus:ring-blue-500'
          />
        </div>

        <select
          name='role_id'
          value={updatedUser.role_id}
          onChange={handleInputChange}
          className='text-white w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring focus:ring-blue-500'
        >
          <option value={2}>Staff</option>
          <option value={3}>Owner</option>
        </select>

        <input
          type='date'
          name='date_of_birth'
          value={updatedUser.date_of_birth}
          onChange={handleInputChange}
          className='text-white w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring focus:ring-blue-500'
        />

        <div className='flex justify-end space-x-2'>
          <button
            onClick={onClose}
            className='px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600'
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || emailError}
            className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed relative'
          >
            {isLoading ? (
              <>
                <span className='absolute inset-0 flex items-center justify-center'>
                  <svg
                    className='animate-spin h-5 w-5 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                </span>
                <span className='opacity-0'>Update</span>
              </>
            ) : (
              'Update'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserEditModal;
